import React, { useState, useEffect, useRef } from 'react';
import { Input, Button, Card, Typography, Space, Spin, Avatar, message, Popconfirm } from 'antd';
import { SendOutlined, MailOutlined, DeleteOutlined, RobotOutlined, UserOutlined } from '@ant-design/icons';
import { embotAPI } from '../../services/api';

const { Text, Paragraph } = Typography;

export default function EMCompose() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const bottomRef = useRef(null);

  useEffect(() => {
    loadHistory();
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await embotAPI.history();
      const hist = (res.data || []).map(m => ({
        role: m.role,
        content: m.content,
        actions: m.actions,
        created_at: m.created_at,
      }));
      setMessages(hist);
    } catch { /* ignore */ }
    setHistoryLoading(false);
  };

  const handleSend = async () => {
    const msg = input.trim();
    if (!msg || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setLoading(true);

    try {
      const history = messages.map(m => ({ role: m.role, content: m.content }));
      const res = await embotAPI.chat({ message: msg, conversation_history: history });
      const data = res.data;
      if (data.ok) {
        setMessages(prev => [...prev, { role: 'assistant', content: data.reply, actions: data.actions }]);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: data.error || 'Bot unavailable' }]);
      }
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: `Error: ${e.message}` }]);
    }
    setLoading(false);
  };

  const handleClear = async () => {
    try {
      await embotAPI.clearHistory();
      setMessages([]);
      message.success('History cleared');
    } catch { message.error('Failed to clear'); }
  };

  const renderActionResult = (action) => {
    if (!action?.result) return null;
    const r = action.result;
    const t = action.type;

    if (t === 'send_email' && r.ok) {
      return <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 8, padding: '8px 12px', marginTop: 8 }}>
        <Text type="success">Sent: {r.sent}, Failed: {r.failed}</Text>
      </div>;
    }
    if (t === 'check_inbox' && r.messages) {
      return <div style={{ background: '#e6f4ff', border: '1px solid #91caff', borderRadius: 8, padding: '8px 12px', marginTop: 8, maxHeight: 200, overflowY: 'auto' }}>
        <Text strong>Inbox ({r.total} total, {r.unread} unread)</Text>
        {r.messages.slice(0, 10).map((m, i) => (
          <div key={i} style={{ borderBottom: '1px solid #f0f0f0', padding: '4px 0', fontSize: 12 }}>
            <div><Text strong>{m.from}</Text></div>
            <div>{m.subject}</div>
            <div><Text type="secondary">{m.date}</Text></div>
          </div>
        ))}
      </div>;
    }
    if (t === 'add_contact' && r.ok) {
      return <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 8, padding: '8px 12px', marginTop: 8 }}>
        <Text type="success">Contact added: {r.name} (ID: {r.id})</Text>
      </div>;
    }
    if (t === 'campaign_send' && r.ok) {
      return <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 8, padding: '8px 12px', marginTop: 8 }}>
        <Text type="success">Campaign sent! {r.sent}/{r.total} delivered, {r.failed} failed</Text>
      </div>;
    }
    if (t === 'get_stats' && r.ok) {
      return <div style={{ background: '#e6f4ff', border: '1px solid #91caff', borderRadius: 8, padding: '8px 12px', marginTop: 8 }}>
        <Text>Contacts: {r.total_contacts} | Sent: {r.total_sent} | Open rate: {r.open_rate}%</Text>
      </div>;
    }
    if (r.error) {
      return <div style={{ background: '#fff2f0', border: '1px solid #ffccc7', borderRadius: 8, padding: '8px 12px', marginTop: 8 }}>
        <Text type="danger">{r.error}</Text>
      </div>;
    }
    return null;
  };

  const suggestions = [
    'Help me write a promotional email for AlayaEss',
    'Check inbox for jeff@comboware.tw',
    'Show me email marketing stats',
    'Add contact: Wang 王大明 wang@example.com',
  ];

  if (historyLoading) return <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 360px)', minHeight: 400 }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <Popconfirm title="Clear all chat history?" onConfirm={handleClear} okText="Clear" cancelText="Cancel">
          <Button size="small" icon={<DeleteOutlined />} danger>Clear History</Button>
        </Popconfirm>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 0', minHeight: 0 }}>
        {messages.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <MailOutlined style={{ fontSize: 48, color: '#00b4d8', marginBottom: 16 }} />
            <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>EMBot - AI Email Marketing</div>
            <div style={{ color: '#666', marginBottom: 24 }}>Ask me to write emails, check inbox, manage contacts, or run campaigns.</div>
            <Space wrap>
              {suggestions.map((s, i) => (
                <Button key={i} size="small" onClick={() => { setInput(s); }}>{s}</Button>
              ))}
            </Space>
          </div>
        )}

        {messages.map((msg, idx) => (
          <div key={idx} style={{
            display: 'flex', gap: 12, marginBottom: 16,
            flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
            alignItems: 'flex-start',
          }}>
            <Avatar
              size={32}
              style={{ background: msg.role === 'user' ? '#1890ff' : '#00b4d8', flexShrink: 0 }}
              icon={msg.role === 'user' ? <UserOutlined /> : <RobotOutlined />}
            />
            <div style={{
              maxWidth: '75%',
              background: msg.role === 'user' ? '#e6f4ff' : '#f5f5f5',
              borderRadius: 12,
              padding: '10px 14px',
            }}>
              <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 14, lineHeight: 1.6 }}>
                {msg.content}
              </div>
              {msg.actions?.map((a, i) => <div key={i}>{renderActionResult(a)}</div>)}
            </div>
          </div>
        ))}

        {loading && (
          <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
            <Avatar size={32} style={{ background: '#00b4d8' }} icon={<RobotOutlined />} />
            <div style={{ background: '#f5f5f5', borderRadius: 12, padding: '12px 16px' }}><Spin size="small" /> Thinking...</div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <div style={{ display: 'flex', gap: 8, paddingTop: 12, borderTop: '1px solid #f0f0f0' }}>
        <Input.TextArea
          value={input}
          onChange={e => setInput(e.target.value)}
          onPressEnter={e => { if (!e.shiftKey) { e.preventDefault(); handleSend(); } }}
          placeholder="Tell EMBot what you need... (Shift+Enter for newline)"
          autoSize={{ minRows: 1, maxRows: 4 }}
          style={{ flex: 1 }}
        />
        <Button type="primary" icon={<SendOutlined />} onClick={handleSend} loading={loading} style={{ height: 'auto', background: '#00b4d8', borderColor: '#00b4d8' }}>
          Send
        </Button>
      </div>
    </div>
  );
}
